import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { SurveySession } from '../survey-session/survey-session.class';
import {
  SurveyListInstruction,
  SurveyListOption,
} from '@hyper-inferno/survey-elements-core';
import {
  ITransformationContext,
  QuestionHandler,
} from '../survey-engine/survey-engine.class';
import { differenceBy, shuffle, uniqBy } from 'lodash';

export class ListInstructionProcessor {
  /**
   * Main entry point to transform instructions into a final list of options.
   */
  public static process(
    listId: string,
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    const { bundle, session, handlers } = ctx;
    const listDef = bundle.getListByListIdQuery(listId);
    if (!listDef) return [];

    const instructions = listDef.instructions || [];
    let currentOptions: SurveyListOption[] = [];

    // Process each instruction in sequence
    for (const inst of instructions) {
      currentOptions = this.executeInstruction(
        inst,
        currentOptions,
        bundle,
        session,
        handlers,
      );
    }

    // Apply global randomization if requested and not already handled by a specific instruction
    // if (
    //   randomizeRequested &&
    //   !instructions.some((i) => i.function === 'RANDOMIZE')
    // ) {
    //   currentOptions = shuffle(currentOptions);
    // }

    return currentOptions;
  }

  private static executeInstruction(
    inst: SurveyListInstruction,
    currentOptions: SurveyListOption[],
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): any[] {
    const func = inst.function;

    switch (func) {
      case 'ADD':
      case 'AIC':
      case 'ANC': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
        );
        const position = this.resolvePosition(inst, currentOptions.length);

        // Insert items at the calculated position
        const newList = [...currentOptions];
        newList.splice(position, 0, ...sourceItems);
        return uniqBy(newList, 'id');
      }

      case 'REMOVE': {
        const itemsToRemove = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
        );
        return differenceBy(currentOptions, itemsToRemove, 'id');
      }

      case 'RIC': {
        const chosenIds =
          this.getAnswer(
            inst.function_question_id!,
            bundle,
            session,
            handlers,
          ) || [];
        return currentOptions.filter((opt) => !chosenIds.includes(opt.id));
      }

      case 'RANDOMIZE': {
        const specifyRange = inst.parameter_1 === 'true';
        if (!specifyRange) return shuffle(currentOptions);

        const start = Math.max(0, parseInt(inst.parameter_2 || '1') - 1);
        const end = Math.min(
          currentOptions.length,
          parseInt(inst.parameter_3 || String(currentOptions.length)),
        );

        const prefix = currentOptions.slice(0, start);
        const mid = shuffle(currentOptions.slice(start, end));
        const suffix = currentOptions.slice(end);
        return [...prefix, ...mid, ...suffix];
      }

      case 'LIST_LENGTH': {
        const limit = parseInt(inst.parameter_1 ?? '');
        if (isNaN(limit)) return currentOptions;
        // Note: For a survey UI, 'atleast' usually implies validation,
        // while 'atmost/exactly' implies truncation here.
        return currentOptions.slice(0, limit);
      }

      default:
        return currentOptions;
    }
  }
  static getAnswer(
    function_question_id: number,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): string[] {
    const element = bundle.fetchElementById(function_question_id);
    if (!element) return [];

    // Get the handler for that question type (Single Choice, Numeric, etc.)
    const handler = handlers[element.type];
    if (!handler || typeof handler.getValue !== 'function') {
      console.warn(`No getValue implementation for ${element.type}`);
      return [];
    }

    // Get user's answer via the specific handler
    const userValue: number[] = handler.getValue(
      element,
      session.jsn_responses[element.id] ?? {},
    ) as number[];

    const mainList = bundle.getListOfQuestion(element.id);

    const selectedOptions = mainList?.options
      .filter((option) => userValue.includes(option.option_key))
      .map((option) => option.id);

    return selectedOptions ?? [];
  }

  /**
   * Resolves which items to pull based on 'type' (all, single item, range)
   */
  private static getSourceItems(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): SurveyListOption[] {
    let baseSet: SurveyListOption[] = [];

    // Identify Base Set
    if (['ADD', 'REMOVE'].includes(inst.function)) {
      if (!inst.function_list_id) {
        throw new Error('Error IDentifying source list: No function list id');
      }
      const sourceList = bundle.getListByListIdQuery(inst.function_list_id);
      baseSet = sourceList?.options || [];
    } else if (inst.function === 'AIC') {
      const chosenIds =
        this.getAnswer(inst.function_question_id!, bundle, session, handlers) ||
        [];
      if (!inst.function_question_id) {
        throw new Error('Error IDentifying source list: function_question_id');
      }
      const sourceList = bundle.getListOfQuestion(inst.function_question_id); // Assuming this helper exists
      baseSet = (sourceList?.options || []).filter((opt) =>
        chosenIds.includes(opt.id),
      );
    } else if (inst.function === 'ANC') {
      if (!inst.function_question_id) {
        throw new Error('Error IDentifying source list: function_question_id');
      }
      const chosenIds =
        this.getAnswer(inst.function_question_id, bundle, session, handlers) ||
        [];
      const sourceList = bundle.getListOfQuestion(inst.function_question_id);
      baseSet = (sourceList?.options || []).filter(
        (opt) => !chosenIds.includes(opt.id),
      );
    }

    // Apply Sub-Selection (The "Type" logic)
    const selectionType = inst.type; // "all", "single item", "range"
    if (selectionType === 'single item') {
      const idx = parseInt(inst.parameter_4!) - 1;
      return baseSet[idx] ? [baseSet[idx]] : [];
    } else if (selectionType === 'range') {
      const start = parseInt(inst.parameter_5!) - 1;
      const end = parseInt(inst.parameter_6!);
      return baseSet.slice(start, end);
    }

    return baseSet; // default to "all"
  }

  /**
   * Determines WHERE to insert items (parameter_1, parameter_2, parameter_3)
   */
  private static resolvePosition(inst: any, currentLength: number): number {
    const specifyPos = inst.parameter_1 === 'true';
    if (!specifyPos) return currentLength; // Default to end

    const posType = inst.parameter_2; // "top", "bottom", "custom position"
    if (posType === 'top') return 0;
    if (posType === 'bottom') return currentLength;
    if (posType === 'custom position') {
      const custom = parseInt(inst.parameter_3) - 1;
      return Math.max(0, Math.min(currentLength, custom));
    }

    return currentLength;
  }
}
